import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Card, CardRecord } from '../Card/Card'
import { sanitizeHTML } from '../../../../utils/common-utils'

export type CardCarouselProps = {
  componentSpacing?: string
  cardList?: CardRecord[]
  cardCarouselHeadline?: string
  cardDescription?: string
  brandExtension?: string
  testId?: string
}

export const CardCarousel = (props: CardCarouselProps) => {
  const { componentSpacing, cardList, cardCarouselHeadline, cardDescription, brandExtension } =
    props
  const [position, setPosition] = useState(0)
  const [positionOffset, setPositionOffset] = useState(0)
  const [cardWidth, setCardWidth] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [startY, setStartY] = useState(0)
  const [startX, setStartX] = useState(0)
  const CARD_GAP = 20

  const moveLeft = useCallback(
    (jumpAmount: number) => {
      if (position === 0) {
        return
      }
      if (isNaN(jumpAmount) || jumpAmount === 0) {
        setPosition(position - 1)
        return
      }
      if (position - jumpAmount <= 0) {
        setPosition(0)
        return
      }
      setPosition(position - jumpAmount)
    },
    [position]
  )
  const moveRight = useCallback(
    (jumpAmount: number) => {
      if (!cardList || position === cardList.length - 1) {
        return
      }
      if (isNaN(jumpAmount) || jumpAmount === 0) {
        setPosition(position + 1)
        return
      }
      if (position - jumpAmount >= cardList.length) {
        setPosition(cardList.length - 1)
        return
      }
      setPosition(position - jumpAmount)
    },
    [position, cardList]
  )
  const onSwipe = useCallback(
    (deltaX: number, deltaY: number) => {
      if (Math.abs(deltaY) > 100) {
        return
      }
      const numCardsMoved = deltaX / (cardWidth + CARD_GAP)
      if (deltaX > 50) {
        moveLeft(Math.floor(numCardsMoved))
      }
      if (deltaX < -50) {
        moveRight(Math.ceil(numCardsMoved))
      }
    },
    [moveLeft, moveRight, cardWidth]
  )
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!wrapperRef.current?.contains(e.target as HTMLElement)) {
      return
    }
    setIsSwiping(true)
    setStartX(e.touches[0].clientX)
    setStartY(e.touches[0].clientY)
  }, [])

  const handleTouch = useCallback(
    (e: TouchEvent) => {
      if (!wrapperRef.current?.contains(e.target as HTMLElement)) {
        return
      }
      const endX = e.changedTouches[0].clientX
      const deltaX = endX - startX
      setPositionOffset(deltaX)
    },
    [startX, setPositionOffset]
  )

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!wrapperRef.current?.contains(e.target as HTMLElement)) {
        return
      }
      const endX = e.changedTouches[0].clientX
      const endY = e.changedTouches[0].clientY
      const deltaX = endX - startX
      const deltaY = endY - startY
      onSwipe(deltaX, deltaY)
      setPositionOffset(0)
      setIsSwiping(false)
    },
    [startX, startY, onSwipe]
  )

  useEffect(() => {
    window.addEventListener('touchstart', handleTouchStart)
    window.addEventListener('touchmove', handleTouch)
    window.addEventListener('touchend', handleTouchEnd)
    return () => {
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchmove', handleTouch)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchEnd, handleTouch])

  const handleWindowSizeChange = () => {
    if (wrapperRef?.current) {
      const size = wrapperRef.current.getBoundingClientRect()
      if (size.width + 60 < 768) {
        setCardWidth(size.width - 20)
        return
      }
      if (size.width + 60 < 1240) {
        setCardWidth((size.width - 60) / 3)
        return
      }
      setCardWidth((size.width - 40) / 3)
    }
  }

  useEffect(() => {
    handleWindowSizeChange()
    window.addEventListener('resize', handleWindowSizeChange)
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [])
  if (!cardList) {
    return null
  }
  const gridColumnWidth = `${cardWidth}px`
  const gridStyle = {
    gridTemplateColumns: `repeat(${cardList.length}, ${gridColumnWidth})`,
    right: position * (cardWidth + CARD_GAP) - positionOffset,
  }

  const hasDates = cardList.every(card => card.cardContent?.rawDate)

  let sortedByTime = null
  if (hasDates) {
    sortedByTime = cardList.sort(
      (card1, card2) => Number(card2.cardContent?.rawDate) - Number(card1.cardContent?.rawDate)
    )
  }

  return (
    <section
      className={`card-carousel-component ${componentSpacing} ${brandExtension}`}
      data-testid={props.testId ?? null}
    >
      <div className='Grid u-sizeConstrained u-posRelative'>
        {cardCarouselHeadline && <h2 className='u-textColorDefault'>{cardCarouselHeadline}</h2>}
        {cardDescription && (
          <div
            className='card-description Content'
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(cardDescription) }}
          />
        )}
      </div>
      <div
        className={`carousel-CardCarouselContainer--overflowWrapper u-paddingLeft32 u-paddingRight40 ${
          position !== 0 ? 'shifted u-paddingLeft40 u-sm-paddingLeft40 u-md-paddingLeft40 ' : ''
        } u-sizeFull u-sm-paddingRight40 u-md-paddingRight40 u-marginBottom4`}
      >
        <div
          className={`card-list-wrapper u-posRelative u-marginVert24${
            isSwiping ? ' card-list-wrapper--noTransition' : ''
          }`}
          style={gridStyle}
          ref={wrapperRef}
          data-testid='card-list-wrapper'
        >
          {hasDates &&
            sortedByTime?.map((card, index) => (
              <Card key={card.cardId} card={card} onFocus={() => setPosition(index)} />
            ))}
          {!hasDates &&
            cardList.map((card, index) => (
              <Card key={card.cardId} card={card} onFocus={() => setPosition(index)} />
            ))}
        </div>
      </div>
      <div className='card-carousel-controls u-flex u-flexAlignItemsEnd u-sizeConstrained u-marginBottom4 u-posRelative'>
        <button
          className='Button Button--secondary Button--secondary--action-blue Button--icon Button--icon--stroke'
          onClick={() => moveLeft(0)}
          disabled={position === 0}
        >
          <span className='adel-material-symbols' aria-hidden='true' role='img'>
            chevron_left
          </span>
          <span className='u-hiddenVisually'>Slide Left</span>
        </button>
        <button
          className='Button Button--secondary Button--secondary--action-blue Button--icon Button--icon--stroke'
          onClick={() => moveRight(0)}
          disabled={position === cardList.length - 1}
        >
          <span className='adel-material-symbols' aria-hidden='true' role='img'>
            chevron_right
          </span>
          <span className='u-hiddenVisually'>Slide Right</span>
        </button>
        <p className='u-textColorActionBlue'>
          {position + 1} / {cardList.length}
        </p>
      </div>
    </section>
  )
}
